// src/views/AccommodationProvider/ProviderOperationalStandards.js
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Badge, 
  Row, 
  Col, 
  Button, 
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner
} from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ProviderOperationalStandards = () => {
  const navigate = useNavigate();
  const { propertyId } = useParams();
  
  // Get provider information from localStorage
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  // State management
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });
  const [validationErrors, setValidationErrors] = useState({});
  
  // Compliance checklist state - updated field names to match API
  const [complianceChecklist, setComplianceChecklist] = useState({
    // Safety and Security
    secure_doors: false,
    fire_safety: false,
    smoke_alarms: false,
    incident_log: false,
    proper_lighting: false,
    emergency_routes: false,
    
    // Health and Hygiene
    health_audit: false,
    health_records: false,
    clean_water: false,
    waste_management: false,
    pest_prevention: false,
    
    // Additional Standards
    wifi: false,
    ventilation: false,
    office_area: false,
    workers_permits: false,
    
    // Warden/Caretaker
    caretaker_available: false,
    caretaker_room: false,
    caretaker_common_area: false,
    caretaker_kitchen_access: false,
    caretaker_bathroom_access: false
  });

  // Document upload state (for future use)
  // const [files, setFiles] = useState({
  //   health_safety_certificate: null,
  //   ohs_audit: null
  // });

  // Existing documents state (for status display)
  const [existingDocuments, setExistingDocuments] = useState({
    health_safety_certificate: null,
    health_safety_certificate_status: 'not_provided',
    health_safety_certificate_expiry_date: null,
    health_safety_certificate_comments: null,
    ohs_audit: null,
    ohs_audit_status: 'not_provided',
    ohs_audit_expiry_date: null,
    ohs_audit_comments: null
  });

  // Fetch property and existing data on component mount
  useEffect(() => {
    const fetchPropertyData = async () => {
      if (!propertyId) {
        setAlert({
          type: 'danger',
          message: 'Property ID is required',
          visible: true
        });
        setLoading(false);
        return;
      }

      try {
        // Fetch property details
        const propertyResponse = await axiosInstance.get(`/api/properties/${propertyId}/`);
        setProperty(propertyResponse.data);

        // Fetch existing operational standards data
        try {
          const operationalResponse = await axiosInstance.get(`/api/properties/${propertyId}/operational-standards/`);
          
          // Update checklist state with existing data
          const data = operationalResponse.data;
          setComplianceChecklist({
            secure_doors: data.secure_doors || false,
            fire_safety: data.fire_safety || false,
            smoke_alarms: data.smoke_alarms || false,
            incident_log: data.incident_log || false,
            proper_lighting: data.proper_lighting || false,
            emergency_routes: data.emergency_routes || false,
            health_audit: data.health_audit || false,
            health_records: data.health_records || false,
            clean_water: data.clean_water || false,
            waste_management: data.waste_management || false,
            pest_prevention: data.pest_prevention || false,
            wifi: data.wifi || false,
            ventilation: data.ventilation || false,
            office_area: data.office_area || false,
            workers_permits: data.workers_permits || false,
            caretaker_available: data.caretaker_available || false,
            caretaker_room: data.caretaker_room || false,
            caretaker_common_area: data.caretaker_common_area || false,
            caretaker_kitchen_access: data.caretaker_kitchen_access || false,
            caretaker_bathroom_access: data.caretaker_bathroom_access || false
          });
        } catch (opError) {
          console.log('No existing operational standards found, starting with defaults');
        }

        // Fetch existing documents
        try {
          const documentsResponse = await axiosInstance.get(`/api/properties/${propertyId}/operational-standards/documents/`);
          setExistingDocuments(documentsResponse.data);
        } catch (docError) {
          console.log('No existing documents found, starting with defaults');
        }

      } catch (error) {
        console.error('Error fetching property data:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to load property information. Please try again.',
          visible: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyData();
  }, [propertyId]);

  // Handle checkbox changes
  const handleCheckboxChange = (field) => {
    setComplianceChecklist(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
    
    // Clear validation error for this field when checked
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  // Validate all checkboxes are checked
  const validateChecklist = () => {
    const errors = {};
    let hasErrors = false;

    // Check all fields in the checklist
    Object.keys(complianceChecklist).forEach(field => {
      if (!complianceChecklist[field]) {
        errors[field] = true;
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  };

  // Handle file uploads (commented out until backend is ready)
  // const handleFileChange = async (documentType, event) => {
  //   const file = event.target.files[0];
  //   if (!file) return;

  //   // Update the state first to show the selected file
  //   setFiles(prevFiles => ({
  //     ...prevFiles,
  //     [documentType]: file
  //   }));

  //   // TODO: Auto-upload the file when backend is ready
  //   // await uploadOperationalDocument(file, documentType);
    
  //   // For now, just show success message
  //   setAlert({
  //     type: 'success',
  //     message: `${documentType === 'health_safety_certificate' ? 'Health & Safety Certificate' : 'OHS Audit'} selected successfully. Backend upload will be implemented.`,
  //     visible: true
  //   });
  // };

  // Save compliance checklist
  const handleSaveChecklist = async () => {
    // Validate all checkboxes are checked
    if (!validateChecklist()) {
      setAlert({
        type: 'danger',
        message: 'Please ensure all compliance standards are met before saving. All checkboxes must be checked.',
        visible: true
      });
      
      // Scroll to top to show the alert
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    try {
      // Save checklist to backend
      const response = await axiosInstance.post(`/api/properties/${propertyId}/operational-standards/`, complianceChecklist);
      
      setAlert({
        type: 'success',
        message: response.data.message || 'Compliance checklist saved successfully! All operational standards have been verified.',
        visible: true
      });
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error saving checklist:', error);
      
      let errorMessage = 'Failed to save compliance checklist. Please try again.';
      
      // Handle validation errors from backend
      if (error.response?.data?.details) {
        const validationErrors = error.response.data.details;
        const errorFields = Object.keys(validationErrors);
        
        if (errorFields.length > 0) {
          errorMessage = `Validation failed: ${errorFields.join(', ')} must be checked.`;
          
          // Set validation errors in state to highlight fields
          const fieldErrors = {};
          errorFields.forEach(field => {
            fieldErrors[field] = true;
          });
          setValidationErrors(fieldErrors);
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      setAlert({
        type: 'danger',
        message: errorMessage,
        visible: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (file, documentType) => {
    if (!file) return;

    setIsSubmitting(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append(documentType, file);

      // Upload document
      const response = await axiosInstance.post(
        `/api/properties/${propertyId}/operational-standards/documents/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Update existing documents state
      setExistingDocuments(response.data.data);

      setAlert({
        type: 'success',
        message: `${documentType === 'health_safety_certificate' ? 'Health & Safety Certificate' : 'OHS Audit'} uploaded successfully and is pending approval.`,
        visible: true
      });

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error uploading document:', error);

      let errorMessage = 'Failed to upload document. Please try again.';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      setAlert({
        type: 'danger',
        message: errorMessage,
        visible: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get status badge component
  const getStatusBadge = (status) => {
    const statusConfig = {
      'not_provided': { color: 'secondary', text: 'Not Provided' },
      'pending_approval': { color: 'warning', text: 'Pending Approval' },
      'rejected': { color: 'danger', text: 'Rejected' },
      'accepted': { color: 'success', text: 'Accepted' }
    };
    
    const config = statusConfig[status] || statusConfig['not_provided'];
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  // Handle back navigation
  const handleBackToInspection = () => {
    navigate('/provider-request-inspection');
  };

  // Handle next navigation
  const handleNextToPoliciesInsurances = () => {
    navigate(`/provider-policies-insurances/${propertyId}`);
  };

  // Show loading spinner
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-3">Loading operational standards...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <Row>
        <Col>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">Operational Standards - {property?.trading_name || 'Property'}</h2>
              {representativeName && (
                <p className="text-muted mb-1">Representative: {representativeName}</p>
              )}
              <span className="me-2 badge bg-info">Business/Organization</span>
              <span className="badge bg-primary">Compliance Standards</span>
            </div>
            <Button size="sm" color="outline-secondary" onClick={handleBackToInspection}>
              <i className="bi bi-arrow-left me-2"></i>
              Back to Request Inspection
            </Button>
          </div>

          {/* Alert */}
          {alert.visible && (
            <Alert 
              color={alert.type} 
              toggle={() => setAlert({...alert, visible: false})} 
              className="mb-4"
            >
              {alert.message}
            </Alert>
          )}

          {/* Information Alert */}
          <Alert color="info" className="mb-3">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Complete Operational Standards:</strong> Check all applicable safety, security, and hygiene standards that your property meets. Upload required compliance documents.
          </Alert>

          {/* Document Status Information */}
          <Alert color="light" className="border mb-4">
            <h6 className="mb-2">
              <i className="bi bi-clipboard-check me-2"></i>
              Document Review Status
            </h6>
            <ul className="mb-0 small">
              <li><Badge color="warning">Pending Approval</Badge> - Your document is being reviewed by our team</li>
              <li><Badge color="success">Accepted</Badge> - Document approved. Expiry date shown if applicable</li>
              <li><Badge color="danger">Rejected</Badge> - Document needs correction. See rejection reason below</li>
            </ul>
          </Alert>

          <Row>
            {/* Full Width: Compliance Checkboxes */}
            <Col md="12">
              <Card>
                <CardBody>
                  <CardTitle tag="h4" className="mb-4">
                    <i className="bi bi-check2-square me-2 text-primary"></i>
                    Operational Standards Checklist
                  </CardTitle>

                  {/* Safety and Security Section */}
                  <div className="p-3 rounded mb-3" style={{backgroundColor: '#f8f9fa', borderLeft: '4px solid #007bff'}}>
                    <h5 className="text-primary mb-3">
                      <i className="bi bi-shield-fill-check me-2"></i>
                      Safety and Security
                    </h5>
                    <div className="border rounded p-3 bg-white">
                      {[
                        { key: 'secure_doors', label: 'Secure doors and windows' },
                        { key: 'fire_safety', label: 'Fire extinguishers' },
                        { key: 'smoke_alarms', label: 'Smoke detectors with control panel' },
                        { key: 'incident_log', label: 'Occurrence book' },
                        { key: 'proper_lighting', label: 'Adequate lighting in and around the premises' },
                        { key: 'emergency_routes', label: 'Emergency exits' }
                      ].map((item) => (
                        <FormGroup check className="mb-3" key={item.key}>
                          <Input
                            type="checkbox"
                            id={item.key}
                            checked={complianceChecklist[item.key]}
                            onChange={() => handleCheckboxChange(item.key)}
                            disabled={regStatus !== '2' || isSubmitting}
                            className={validationErrors[item.key] ? 'border-danger' : ''}
                          />
                          <Label 
                            check 
                            for={item.key} 
                            className={`ms-2 fw-bold ${validationErrors[item.key] ? 'text-danger' : ''}`}
                          >
                            {item.label}
                          </Label>
                          {validationErrors[item.key] && (
                            <div className="text-danger small mt-1 ms-4">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              This safety standard is required
                            </div>
                          )}
                        </FormGroup>
                      ))}
                    </div>
                  </div>

                  {/* Health and Hygiene Section */}
                  <div className="p-3 rounded mb-3" style={{backgroundColor: '#f8f9fa', borderLeft: '4px solid #28a745'}}>
                    <h5 className="text-success mb-3">
                      <i className="bi bi-heart-pulse me-2"></i>
                      Health and Hygiene
                    </h5>
                    <div className="border rounded p-3 bg-white">
                      {[
                        { key: 'health_audit', label: 'Annual OHS audit' },
                        { key: 'health_records', label: 'OHS file on premises' },
                        { key: 'clean_water', label: 'Access to clean water and sanitation' },
                        { key: 'waste_management', label: 'Regular waste removal' },
                        { key: 'pest_prevention', label: 'Pest control in place' }
                      ].map((item) => (
                        <FormGroup check className="mb-3" key={item.key}>
                          <Input
                            type="checkbox"
                            id={item.key}
                            checked={complianceChecklist[item.key]}
                            onChange={() => handleCheckboxChange(item.key)}
                            disabled={regStatus !== '2' || isSubmitting}
                            className={validationErrors[item.key] ? 'border-danger' : ''}
                          />
                          <Label 
                            check 
                            for={item.key} 
                            className={`ms-2 fw-bold ${validationErrors[item.key] ? 'text-danger' : ''}`}
                          >
                            {item.label}
                          </Label>
                          {validationErrors[item.key] && (
                            <div className="text-danger small mt-1 ms-4">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              This hygiene standard is required
                            </div>
                          )}
                        </FormGroup>
                      ))}
                    </div>
                  </div>

                  {/* Additional Standards Section */}
                  <div className="p-3 rounded mb-3" style={{backgroundColor: '#f8f9fa', borderLeft: '4px solid #17a2b8'}}>
                    <h5 className="text-info mb-3">
                      <i className="bi bi-gear-fill me-2"></i>
                      Additional Standards
                    </h5>
                    <div className="border rounded p-3 bg-white">
                      {[
                        { key: 'wifi', label: 'Wi-Fi' },
                        { key: 'ventilation', label: 'Natural Room Ventilation' },
                        { key: 'office_area', label: 'Office/Reception Area' },
                        { key: 'workers_permits', label: "Workers' health & safety permits" }
                      ].map((item) => (
                        <FormGroup check className="mb-3" key={item.key}>
                          <Input
                            type="checkbox"
                            id={item.key}
                            checked={complianceChecklist[item.key]}
                            onChange={() => handleCheckboxChange(item.key)}
                            disabled={regStatus !== '2' || isSubmitting}
                            className={validationErrors[item.key] ? 'border-danger' : ''}
                          />
                          <Label 
                            check 
                            for={item.key} 
                            className={`ms-2 fw-bold ${validationErrors[item.key] ? 'text-danger' : ''}`}
                          >
                            {item.label}
                          </Label>
                          {validationErrors[item.key] && (
                            <div className="text-danger small mt-1 ms-4">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              This additional standard is required
                            </div>
                          )}
                        </FormGroup>
                      ))}
                    </div>
                  </div>

                  {/* Warden/Caretaker Section */}
                  <div className="p-3 rounded mb-3" style={{backgroundColor: '#f8f9fa', borderLeft: '4px solid #6f42c1'}}>
                    <h5 className="mb-3" style={{color: '#6f42c1'}}>
                      <i className="bi bi-person-badge me-2"></i>
                      Warden/Caretaker
                    </h5>
                    <div className="border rounded p-3 bg-white">
                      {[
                        { key: 'caretaker_available', label: 'Caretaker on premises 24/7' },
                        { key: 'caretaker_room', label: 'Caretaker has private bedroom' },
                        { key: 'caretaker_common_area', label: 'Caretaker has private lounge' },
                        { key: 'caretaker_kitchen_access', label: 'Caretaker has private kitchen' },
                        { key: 'caretaker_bathroom_access', label: 'Caretaker has private bathroom' }
                      ].map((item) => (
                        <FormGroup check className="mb-3" key={item.key}>
                          <Input
                            type="checkbox"
                            id={item.key}
                            checked={complianceChecklist[item.key]}
                            onChange={() => handleCheckboxChange(item.key)}
                            disabled={regStatus !== '2' || isSubmitting}
                            className={validationErrors[item.key] ? 'border-danger' : ''}
                          />
                          <Label 
                            check 
                            for={item.key} 
                            className={`ms-2 fw-bold ${validationErrors[item.key] ? 'text-danger' : ''}`}
                          >
                            {item.label}
                          </Label>
                          {validationErrors[item.key] && (
                            <div className="text-danger small mt-1 ms-4">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              This caretaker requirement is required
                            </div>
                          )}
                        </FormGroup>
                      ))}
                    </div>
                  </div>

                  {/* Save Progress Button */}
                  <div className="text-center mt-4">
                    <Button 
                      color="primary" 
                      size="lg" 
                      onClick={handleSaveChecklist}
                      disabled={regStatus !== '2' || isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          Save Compliance Checklist
                        </>
                      )}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Document Upload Section - Horizontally Aligned */}
          <Row className="mt-5">
            <Col>
              <h3 className="text-primary mb-4">
                <i className="bi bi-cloud-upload me-2"></i>
                Upload Required Documents
              </h3>
              
              <Row>
                {/* Health and Safety Compliance Certificate */}
                <Col md="6">
                  <Card>
                    <CardBody>
                      <CardTitle tag="h4" className="mb-4">
                        <i className="bi bi-file-earmark-medical me-2 text-success"></i>
                        Health & Safety Compliance Certificate
                      </CardTitle>

                      {/* Document Status */}
                      <Row className="mb-4">
                        <Col md="8" className="mx-auto">
                          <div className="p-3 rounded" style={{backgroundColor: '#f8f9fa', border: '1px solid #dee2e6'}}>
                            <h6 className="mb-2">
                              <i className="bi bi-file-text me-2"></i>
                              Document Status
                            </h6>
                            {getStatusBadge(existingDocuments.health_safety_certificate_status)}
                              
                              {/* Display expiry date if approved */}
                              {existingDocuments.health_safety_certificate_status === 'accepted' && existingDocuments.health_safety_certificate_expiry_date && (
                                <div className="mt-2">
                                  <small className="text-success">
                                    <i className="bi bi-calendar-check me-1"></i>
                                    Valid until: {new Date(existingDocuments.health_safety_certificate_expiry_date).toLocaleDateString()}
                                  </small>
                                </div>
                              )}
                              
                              {/* Display rejection comments if rejected */}
                              {existingDocuments.health_safety_certificate_status === 'rejected' && existingDocuments.health_safety_certificate_comments && (
                                <div className="mt-2">
                                  <small className="text-danger">
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    <strong>Rejection reason:</strong> {existingDocuments.health_safety_certificate_comments}
                                  </small>
                                </div>
                              )}
                          </div>
                        </Col>
                      </Row>

                      {/* File Input */}
                      <FormGroup className="mb-3">
                        <Label for="healthSafetyCert">
                          <strong>Upload Health & Safety Certificate</strong>
                        </Label>
                        <Input
                          type="file"
                          id="healthSafetyCert"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleDocumentUpload(file, 'health_safety_certificate');
                            }
                          }}
                          disabled={regStatus !== '2' || isSubmitting}
                        />
                      </FormGroup>
                      
                      <div className="text-center">
                        <small className="text-muted">
                          Documents will upload automatically when you select them.
                        </small>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Annual OHS Audit */}
                <Col md="6">
                  <Card>
                    <CardBody>
                      <CardTitle tag="h4" className="mb-4">
                        <i className="bi bi-file-earmark-check me-2 text-warning"></i>
                        Annual OHS Audit Report
                      </CardTitle>

                      {/* Document Status */}
                      <Row className="mb-4">
                        <Col md="8" className="mx-auto">
                          <div className="p-3 rounded" style={{backgroundColor: '#f8f9fa', border: '1px solid #dee2e6'}}>
                            <h6 className="mb-2">
                              <i className="bi bi-file-text me-2"></i>
                              Document Status
                            </h6>
                            {getStatusBadge(existingDocuments.ohs_audit_status)}
                              
                              {/* Display expiry date if approved */}
                              {existingDocuments.ohs_audit_status === 'accepted' && existingDocuments.ohs_audit_expiry_date && (
                                <div className="mt-2">
                                  <small className="text-success">
                                    <i className="bi bi-calendar-check me-1"></i>
                                    Valid until: {new Date(existingDocuments.ohs_audit_expiry_date).toLocaleDateString()}
                                  </small>
                                </div>
                              )}
                              
                              {/* Display rejection comments if rejected */}
                              {existingDocuments.ohs_audit_status === 'rejected' && existingDocuments.ohs_audit_comments && (
                                <div className="mt-2">
                                  <small className="text-danger">
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    <strong>Rejection reason:</strong> {existingDocuments.ohs_audit_comments}
                                  </small>
                                </div>
                              )}
                          </div>
                        </Col>
                      </Row>

                      {/* File Input */}
                      <FormGroup className="mb-3">
                        <Label for="ohsAudit">
                          <strong>Upload Annual OHS Audit</strong>
                        </Label>
                        <Input
                          type="file"
                          id="ohsAudit"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleDocumentUpload(file, 'ohs_audit');
                            }
                          }}
                          disabled={regStatus !== '2' || isSubmitting}
                        />
                      </FormGroup>
                      
                      <div className="text-center">
                        <small className="text-muted">
                          Documents will upload automatically when you select them.
                        </small>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row className="mt-4">
            <Col className="text-center">
              <Button color="outline-secondary" className="me-3" onClick={handleBackToInspection}>
                <i className="bi bi-arrow-left me-2"></i>
                Previous: Request Inspection
              </Button>
              <Button color="outline-secondary" className="me-3" onClick={handleSaveChecklist} disabled={regStatus !== '2' || isSubmitting}>
                <i className="bi bi-save me-2"></i>
                Save Draft
              </Button>
              <Button color="primary" size="lg" className="px-4" onClick={handleNextToPoliciesInsurances}>
                Next: Policies & Insurances
                <i className="bi bi-arrow-right ms-2"></i>
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default ProviderOperationalStandards;